import { Menu } from "@headlessui/react";
import {
  CheckIcon,
  ExclamationCircleIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import { ReactChild, ReactChildren, useState } from "react";
import { CreateManualScrapePostResponse, ItemWithMetaData } from "../api/Items";
import { SCRAPE_STATUS } from "../api/Profiles";
import Badge, { BadgeColors } from "../shared-components/Badge";
import Button, { ButtonColorTypes } from "../shared-components/Button";

import EllipsisIcon from "../shared-components/icons/EllipsisIcon";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { Modal } from "../shared-components/Modal";
import { ManualScrapeCreatedPostsModal } from "./ManualScrapeCreatedPostsModal";
import { ManualScrapeSkippedPostsModal } from "./ManualScrapeSkippedPostsModal";

export enum ManualProfileRowType {
  SYSTEM,
  SKIPPED,
  MANUAL,
}

export function ManualProfileRowDataBadge({
  status,
  children,
}: {
  status: SCRAPE_STATUS;
  children?: ReactChild | ReactChildren;
}) {
  function buildBadgeProps(): { color: BadgeColors; text: string } {
    switch (status) {
      case SCRAPE_STATUS.FAILED:
        return { color: "red", text: "Failed" };

      case SCRAPE_STATUS.DONE:
        return { color: "default", text: "Complete" };
      case SCRAPE_STATUS.IN_PROGRESS:
      default:
        return { color: "blue", text: "In Progress" };
    }
  }
  const badgeProps = buildBadgeProps();
  return (
    <Badge hasDot={false} color={badgeProps.color}>
      <>
        {children}
        {badgeProps.text}
      </>
    </Badge>
  );
}

export interface RowDataProps {
  profile: ItemWithMetaData;
  rowType: ManualProfileRowType;
  isSalvageYardConnected: boolean;
  storingPosts: boolean;
  scrapeError: string;
  isReadOnly: boolean;
  onAction: (action: MANUAL_PROFILE_ACTION) => void;
  storedPosts?: CreateManualScrapePostResponse[];
  skippedPosts?: { url: string; text?: string; reason?: string }[];
  disableStart: boolean;
}

function DotMenu({
  onSkip,
  onRestart,
}: {
  onSkip: () => void;
  onRestart: () => void;
}) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  function handleClose() {
    setIsModalOpen(false);
  }
  return (
    <>
      <Menu as="div" className="relative flex justify-center items-center">
        <Menu.Button>
          <EllipsisIcon className="ml-4 cursor-pointer text-gray-800" />
        </Menu.Button>
        <Menu.Items className="absolute right-0 mt-2 w-24 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <Menu.Item>
            <div
              className="p-2 text-center cursor-pointer"
              onClick={() => onRestart()}
            >
              Restart
            </div>
          </Menu.Item>
          <Menu.Item>
            <div
              className="p-2 text-center cursor-pointer"
              onClick={() => setIsModalOpen(true)}
            >
              Skip
            </div>
          </Menu.Item>
        </Menu.Items>
      </Menu>
      <Modal open={isModalOpen} onClose={handleClose} width={"w-104 p-3"}>
        <div>
          <div className="flex gap-4 mb-4">
            <div className="flex justify-center w-9 h-9 rounded-full bg-red-100">
              <ExclamationCircleIcon className="w-6 text-red-500" />
            </div>
            <div className="flex flex-col">
              <span className="text-lg font-bold mb-2">Skip Profile?</span>
              <span className="text-sm text-gray-400">
                By doing this you will skip profile and not be able to work on
                it.
              </span>
            </div>
          </div>
          <div className="flex gap-4 justify-end">
            <Button
              text={"Cancel"}
              colorType={ButtonColorTypes.cancel}
              onClick={handleClose}
            />
            <Button
              text="Ok"
              colorType={ButtonColorTypes.secondary}
              onClick={onSkip}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

export enum MANUAL_PROFILE_ACTION {
  START,
  SKIP,
  COMPLETE,
  RESET,
  RESTART,
}

export function ManualProfileRowData({
  profile,
  rowType,
  isSalvageYardConnected,
  storingPosts,
  scrapeError,
  isReadOnly,
  onAction,
  storedPosts,
  skippedPosts,
  disableStart,
}: RowDataProps) {
  const status = profile.item.content.scrape_status;
  switch (rowType) {
    case ManualProfileRowType.MANUAL:
      return (
        <div className="flex justify-center items-center">
          {status === SCRAPE_STATUS.DONE && !storingPosts && (
            <>
              <div className="flex gap-2 text-sm bg-green-600 rounded text-white px-4 py-2 opacity-50">
                <CheckIcon className="w-4" />
                <span className="text-white">Complete</span>
              </div>
              <ManualScrapeCreatedPostsModal storedPosts={storedPosts} />
              <ManualScrapeSkippedPostsModal skippedPosts={skippedPosts} />
            </>
          )}
          {status === SCRAPE_STATUS.IN_PROGRESS &&
            !storingPosts &&
            !scrapeError && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <GlobeAltIcon className="text-green-600 animate-spin w-4" />
                <span>In progress</span>
                <ManualScrapeSkippedPostsModal skippedPosts={skippedPosts} />
              </div>
            )}
          {status === SCRAPE_STATUS.IN_PROGRESS &&
            storingPosts &&
            !scrapeError && (
              <div className="flex gap-4 text-sm text-gray-500">
                <span className="mt-1">Saving Posts</span>
                <LoadingSpinner className="text-green-600 w-6" />
                <ManualScrapeSkippedPostsModal skippedPosts={skippedPosts} />
              </div>
            )}
          {(!status || status === SCRAPE_STATUS.TO_DO) && !scrapeError && (
            <div className="flex items-center gap-3">
              <Button
                text="Start Scrape"
                disabled={isReadOnly || disableStart}
                onClick={() => onAction(MANUAL_PROFILE_ACTION.START)}
              />
            </div>
          )}
          {scrapeError && (
            <span
              title={scrapeError}
              className="flex gap-4 text-sm text-gray-500"
            >
              <span>Scrape Error</span>
              <ExclamationCircleIcon className="text-red-600 w-6" />
            </span>
          )}
          {
            <DotMenu
              onSkip={() => onAction(MANUAL_PROFILE_ACTION.SKIP)}
              onRestart={() => onAction(MANUAL_PROFILE_ACTION.RESTART)}
            ></DotMenu>
          }
        </div>
      );
    case ManualProfileRowType.SKIPPED:
      return (
        <div className="text-gray-600">
          <span>Skipped</span>
          {/* <span className="pl-2">tbd...</span>  TODO: enable once the information is available */}
        </div>
      );
    case ManualProfileRowType.SYSTEM:
    default:
      return <ManualProfileRowDataBadge status={status} />;
  }
}
